import forge from 'apis/forge';
import { DesignSystem } from 'dragonmouse-shared/interfaces';
import { useCallback } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { selectUser } from 'redux/db/reducers';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { designSystemShareUrl } from 'utils';
import AccessManagerModal from '../AccessManagerModal';

interface ShareDesignSystemModalProps {
	designSystem: DesignSystem;
	onClose: () => void;
}

const ShareDesignSystemModal = ({ designSystem, onClose }: ShareDesignSystemModalProps) => {
	const { user, isMobile } = useSelector((r: RootReducer) => {
		return {
			user: selectUser(r.db),
			isMobile: selectIsMobile(r.mode),
		};
	}, shallowEqual);

	const canNativeShare =
		isMobile && typeof navigator !== 'undefined' && typeof navigator.share === 'function';

	const close = useCallback(() => {
		onClose();
	}, [onClose]);

	return (
		<AccessManagerModal
			title="Share this design system"
			onClose={close}
			userAccess={{
				entityId: designSystem.id,
				getUsers: async (id) => forge.getDesignSystemUsers({ designSystemId: id }),
				grantAccess: async (id, emails) =>
					forge.grantDesignSystemAccess.withToast()({ designSystemId: id, emails }),
				revokeAccess: async (id, email) =>
					forge.revokeDesignSystemAccess.withToast()({ designSystemId: id, email }),
				errorMessages: {
					fetchFailed: 'Failed to fetch design system users',
					revokeFailed: 'Failed to revoke user access',
				},
				onAccessGranted: () => {
					if (user?.id) {
						forge.updateUser({
							update: {
								id: user.id,
								metadata: { lastSharedDate: new Date().toISOString() },
							},
						});
					}
				},
			}}
			organizationAccess={{
				entityId: designSystem.id,
				getOrganizations: (id) => forge.getDesignSystemOrganizations(id),
				addToOrganization: (entityId, orgId) =>
					forge.addDesignSystemToOrganization.withToast()(entityId, orgId),
				removeFromOrganization: (entityId, orgId) =>
					forge.removeDesignSystemFromOrganization.withToast()(entityId, orgId),
				emptyStateMessage: 'This design system is not shared with any teams',
				fetchFailedMessage: 'Failed to load design system teams',
			}}
			copyLink={{
				shareUrl: designSystemShareUrl(designSystem),
				linkLabel: canNativeShare ? 'Share design system link' : 'Copy design system link',
				canNativeShare,
				shareTitle: designSystem.title ?? 'Design system',
				trackAction: canNativeShare ? 'share_design_system_link' : 'copy_design_system_link',
			}}
		/>
	);
};

export default ShareDesignSystemModal;
